export interface InvoiceCreateRow {
  chargeLineId: string;
  customerName: string;
  name: string;
  poNumber?: string;
  kind?: string;
  currency?: string;
  amountPence: number;
  hours?: number;
  lines?: { description: string; qty: number; unitPence: number }[];
  poId?: string;
}

interface OrderLineIdentity {
  reference: string;
  description: string;
}

interface ChargeLineIdentity {
  id: string;
  name: string;
  poId?: string;
}

interface BillableOrderLine extends OrderLineIdentity {
  id: string;
  amountPence?: number;
  remainingPence: number;
}

interface BillableChargeLine extends ChargeLineIdentity {
  qty?: number;
  unitPence?: number;
  remainingPence?: number;
}

export interface InvoiceOrderCandidate {
  poLineId: string;
  reference: string;
  description: string;
  chargeLineId: string;
  basisPence: number;
  remainingPence: number;
}

function normalizedLineLabel(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function agreedBasisPence(line: BillableOrderLine, charge: BillableChargeLine): number {
  const qty = charge.qty ?? 1;
  const capacity = Number.isFinite(qty) && qty > 0 && Number.isInteger(charge.unitPence) && Number(charge.unitPence) > 0
    ? Math.round(qty * Number(charge.unitPence))
    : 0;
  if (Number.isSafeInteger(capacity) && capacity > 0) {
    return Number.isInteger(line.amountPence) && Number(line.amountPence) > 0
      ? Math.min(capacity, Number(line.amountPence))
      : capacity;
  }
  if (Number.isInteger(line.amountPence) && Number(line.amountPence) > 0) return Number(line.amountPence);
  return Math.max(0, Number(charge.remainingPence) || line.remainingPence);
}

export function percentageAmountPence(basisPence: number, percentage: string | number): number {
  const rate = Number(percentage);
  if (!Number.isInteger(basisPence) || basisPence <= 0 || !Number.isFinite(rate) || rate <= 0) return 0;
  return Math.round((basisPence * rate) / 100);
}

export function percentageForAmount(amountPence: number, basisPence: number): string {
  if (!Number.isInteger(amountPence) || amountPence <= 0 || !Number.isInteger(basisPence) || basisPence <= 0) return '';
  return String(Number(((amountPence * 100) / basisPence).toFixed(4)));
}

export function invoiceAmountWithinRemaining(amountPence: number, remainingPence: number): boolean {
  return Number.isInteger(amountPence) && amountPence > 0 && Number.isInteger(remainingPence) && amountPence <= remainingPence;
}

export function matchingOrderChargeLine<T extends ChargeLineIdentity>(
  line: OrderLineIdentity,
  charges: T[],
  purchaseOrderId: string,
): T | undefined {
  const orderCharges = charges.filter((charge) => charge.poId === purchaseOrderId);
  const label = normalizedLineLabel([line.reference, line.description].filter(Boolean).join(' - '));
  const exact = orderCharges.find((charge) => normalizedLineLabel(charge.name) === label);
  if (exact) return exact;
  const description = normalizedLineLabel(line.description);
  const descriptionMatches = orderCharges.filter((charge) => normalizedLineLabel(charge.name) === description);
  return descriptionMatches.length === 1 ? descriptionMatches[0] : undefined;
}

export function invoiceCandidatesForOrder(
  lines: BillableOrderLine[],
  charges: BillableChargeLine[],
  purchaseOrderId: string,
): InvoiceOrderCandidate[] {
  const billableLines = lines.filter((line) => Number.isInteger(line.remainingPence) && line.remainingPence > 0);
  const billableCharges = charges.filter((charge) => charge.poId === purchaseOrderId && Number.isInteger(charge.remainingPence) && Number(charge.remainingPence) > 0);
  if (billableLines.length === 1) {
    const line = billableLines[0];
    return billableCharges.map((charge) => ({
      poLineId: line.id,
      reference: line.reference,
      description: charge.name,
      chargeLineId: charge.id,
      basisPence: agreedBasisPence(line, charge),
      remainingPence: Math.min(line.remainingPence, Number(charge.remainingPence)),
    }));
  }
  return billableLines.flatMap((line) => {
    const charge = matchingOrderChargeLine(line, billableCharges, purchaseOrderId);
    if (!charge) return [];
    return [{
      poLineId: line.id,
      reference: line.reference,
      description: line.description,
      chargeLineId: charge.id,
      basisPence: agreedBasisPence(line, charge),
      remainingPence: Math.min(line.remainingPence, Number(charge.remainingPence)),
    }];
  });
}

export function createInvoiceActions(row: { poId?: string }): { ai: true; poPath?: string } {
  return { ai: true, poPath: row.poId ? `/purchase-orders/${encodeURIComponent(row.poId)}?createInvoice=1` : undefined };
}

export function invoiceCreateInstruction(row: InvoiceCreateRow, period?: { start?: string; end?: string }): string {
  const currency = row.currency || 'GBP';
  const pounds = (pence: number) => (pence / 100).toFixed(2);
  const parts = [
    `Create an invoice for ${row.customerName || 'this customer'}.`,
    `Charge line: ${row.name}. Charge line id: ${row.chargeLineId}.`,
    row.kind ? `Kind: ${row.kind}.` : '',
    row.poNumber ? `Purchase order: ${row.poNumber}.` : '',
    period?.start ? `Period start: ${period.start}.` : '',
    period?.end ? `Period end: ${period.end}.` : '',
    `Currency: ${currency}.`,
  ];
  const lines = row.lines || [];
  if (lines.length) {
    parts.push('Invoice lines, using these figures from the charge line:');
    for (const line of lines) parts.push(`- ${line.description}. Quantity ${line.qty}. Unit price ${pounds(line.unitPence)} ${currency}.`);
  } else if (row.amountPence > 0) {
    parts.push(`Ready amount ${pounds(row.amountPence)} ${currency}.`);
  }
  if (row.hours) parts.push(`Hours: ${row.hours}.`);
  parts.push('Use this charge line. Do not invent a price that is not written here.');
  return parts.filter(Boolean).join('\n');
}
